import { useFeatureFlags } from '@brightdrop/feature-flags-client';
import { useFormat } from '@brightdrop/localization-core';
import { useEffect, useRef } from 'react';

import { useGetBatchEntityQuery } from '~/api/generated-api';
import { useAppDispatch } from '~/app/store';
import useToasts from '~/common/components/toasts/useToasts';
import { LOCALIZATION_FEATURE_FLAG } from '~/common/models/featureFlags.model';

import {
  showActivationCompletionToasts,
  showOstEnrollCompletionToast,
} from './activationProgress.toasts';
import { ActivationBatchStatus } from './activationProgress.types';
import { isBulkBatch } from './batch.utils';
import type { ActivationBatch } from './state/activationProgressSlice';
import {
  markActivationCompleted,
  removeActivationBatch,
  updateActivationBatch,
} from './state/activationProgressSlice';

/** Batch-status poll cadence (matches the legacy device-list polling). */
const POLLING_INTERVAL_MS = 2000;

/**
 * How many consecutive poll errors to tolerate before giving up on a batch.
 * A single transient error (network blip, 502/503) shouldn't tear down the
 * poller — RTK recovers on the next poll — so we only finish (as a wholesale
 * failure) once the errors persist.
 */
const MAX_CONSECUTIVE_POLL_FAILURES = 3;

export type ActivationBatchPollerProps = {
  batch: ActivationBatch;
};

/**
 * Headless poller for a single activation batch. Polls the batch status,
 * reconciles the success/failure counts into the global slice (driving the
 * progress bar), and on a terminal state fires the platform completion toasts
 * and bumps the completion marker. Bulk batches are then kept in a terminal
 * status so the widget can show the in-place completion card; single-device
 * batches (toast only) are removed.
 *
 * Rendered once per active batch by {@link ActivationProgressSnackbar}, outside
 * the collapsible body so polling continues while the card is minimized.
 */
const ActivationBatchPoller = ({ batch }: ActivationBatchPollerProps): null => {
  const dispatch = useAppDispatch();

  const { showSuccessToast, showErrorToast } = useToasts();
  const format = useFormat();
  const { getFlag } = useFeatureFlags();
  const localizationEnabled = Boolean(getFlag(LOCALIZATION_FEATURE_FLAG));
  const toastsRef = useRef({
    showSuccessToast,
    showErrorToast,
    formatCount: (n: number): string =>
      localizationEnabled ? format.number(n, { digits: 0 }) : String(n),
  });
  useEffect(() => {
    toastsRef.current = {
      showSuccessToast,
      showErrorToast,
      formatCount: (n: number): string =>
        localizationEnabled ? format.number(n, { digits: 0 }) : String(n),
    };
  }, [showSuccessToast, showErrorToast, localizationEnabled, format]);

  // Latest counts read inside the polling effect without re-subscribing it.
  const batchRef = useRef(batch);
  useEffect(() => {
    batchRef.current = batch;
  }, [batch]);

  // Guards the terminal handling so the completion toasts fire exactly once.
  const finishedRef = useRef(false);

  // Consecutive poll failures; reset only on a genuinely new successful poll.
  const consecutiveFailuresRef = useRef(0);

  // Timestamp of the last successful response we've already processed. Lets us
  // distinguish a fresh poll result from RTK transiently clearing `error`
  // mid-refetch (before new data lands), which must not reset the streak.
  const lastFulfilledRef = useRef<number | undefined>(undefined);

  const { data, error, fulfilledTimeStamp } = useGetBatchEntityQuery(
    { id: batch.batchId, 'x-bd-organization': batch.orgId },
    {
      // Stop polling once the batch is terminal (kept in state for the card).
      skip:
        !batch.batchId ||
        !batch.orgId ||
        batch.status !== ActivationBatchStatus.IN_PROGRESS,
      pollingInterval: POLLING_INTERVAL_MS,
    }
  );

  useEffect(() => {
    if (finishedRef.current) return;

    const { batchId, total, enrolledVehicleCount } = batchRef.current;

    const finish = (success: number, failure: number, failed: boolean) => {
      finishedRef.current = true;
      dispatch(markActivationCompleted());

      // Keyed off the whole submitted selection, so an activation that mixed a
      // single new device with re-enrollments still reports through the card —
      // a single-device toast has nowhere to show the re-enrollments line.
      if (isBulkBatch(batchRef.current)) {
        // Bulk: the in-place completion card reports the outcome (success,
        // partial, or wholesale failure), so we don't also toast — otherwise a
        // full failure would show both an error toast and the completion card.
        // Retain the batch in a terminal state so the card renders until the
        // user dismisses it.
        dispatch(
          updateActivationBatch({
            batchId,
            changes: {
              success,
              failure,
              status: failed
                ? ActivationBatchStatus.FAILED
                : ActivationBatchStatus.COMPLETED,
            },
          })
        );
      } else if (enrolledVehicleCount != null) {
        // OST mixed enroll (single adapter): report the enrolled vehicles and
        // the adapter activation together in ONE combined toast, then drop the
        // batch — never a separate enroll toast plus a generic activation toast.
        showOstEnrollCompletionToast(
          {
            vehicleCount: enrolledVehicleCount,
            adapterSuccess: success,
            adapterFailure: failure,
          },
          toastsRef.current
        );
        dispatch(removeActivationBatch(batchId));
      } else {
        // Single-device: the outcome is surfaced by a toast, then dropped.
        showActivationCompletionToasts(
          { total, success, failure, failed },
          toastsRef.current
        );
        dispatch(removeActivationBatch(batchId));
      }
    };

    // A genuine terminal failure signalled by the API — give up immediately.
    if (data?.success === false) {
      finish(0, total, true);
      return;
    }

    // Transient poll/query error: keep polling and only give up once the
    // failures persist, so a single blip doesn't abandon a batch that's still
    // processing server-side. When we do finish, report a wholesale failure
    // (success 0) so the error toast is never suppressed.
    if (error) {
      consecutiveFailuresRef.current += 1;
      if (consecutiveFailuresRef.current >= MAX_CONSECUTIVE_POLL_FAILURES) {
        finish(0, total, true);
      }
      return;
    }

    // `error` is falsy but no fresh response has landed yet — RTK can briefly
    // clear `error` while a refetch is in flight (stale/absent `data`). Don't
    // treat that as a success or it would reset the failure streak and keep a
    // stuck batch polling forever during a sustained outage.
    if (
      !fulfilledTimeStamp ||
      fulfilledTimeStamp === lastFulfilledRef.current
    ) {
      return;
    }
    lastFulfilledRef.current = fulfilledTimeStamp;

    // Genuinely new successful response — reset the failure streak.
    consecutiveFailuresRef.current = 0;

    const result = data?.result;
    if (!result) return;

    const successCount = result.details?.successCount;
    const failureCount = result.details?.failureCount;

    // Ids of the devices the batch reported as failed, so a "try again" retry
    // can target only those (see ActivationCompletionCard / useRetryActivationBatch).
    // Deduped + sorted so the comparison below is order-insensitive and the
    // persisted value is stable across polls that return the same ids in a
    // different order. Normalized to [] when the response omits `errors` so a
    // later poll clears any previously-captured ids instead of leaving retry to
    // target stale (possibly since-succeeded) devices.
    const failedPeripheralIds = Array.from(
      new Set(
        result.details?.errors
          ?.map((item) => item.deviceId)
          .filter((id): id is string => typeof id === 'string') ?? []
      )
    ).sort();

    const successChanged =
      typeof successCount === 'number' &&
      successCount !== batchRef.current.success;
    const failureChanged =
      typeof failureCount === 'number' &&
      failureCount !== batchRef.current.failure;
    // Compare by content, not just length: the API can report the same number
    // of failures with different deviceIds between polls, and retry must target
    // the current ones. Both sides are normalized (deduped + sorted), so an
    // index-wise compare is order-insensitive.
    const prevFailedIds = batchRef.current.failedPeripheralIds ?? [];
    const failedIdsChanged =
      failedPeripheralIds.length !== prevFailedIds.length ||
      failedPeripheralIds.some((id, index) => id !== prevFailedIds[index]);
    if (successChanged || failureChanged || failedIdsChanged) {
      dispatch(
        updateActivationBatch({
          batchId,
          changes: {
            ...(typeof successCount === 'number' && { success: successCount }),
            ...(typeof failureCount === 'number' && { failure: failureCount }),
            failedPeripheralIds,
          },
        })
      );
    }

    if (result.status === 'completed') {
      finish(successCount ?? total, failureCount ?? 0, false);
    } else if (result.status === 'failed') {
      finish(successCount ?? 0, failureCount ?? total, true);
    }
  }, [data, error, fulfilledTimeStamp, dispatch]);

  return null;
};

export default ActivationBatchPoller;
